import { Button } from '@/components/ui/button';
import { ConfirmDialog } from '@/components/common/ConfirmDialog';
import { Progress } from '@/components/ui/progress';
import { Skeleton } from '@/components/ui/skeleton';
import { useTaxYear } from '@/contexts/TaxYearContext';
import { FORM_FILING_TYPE } from '@/constants/formConstants';
import { deleteForm, getFormById, updateForm } from '@/services/formsApi';
import type { FormResponse } from '@/services/formsApi';
import type { IndividualFormData } from '@/types/form';
import type { BusinessFormData } from '@/types/form';
import { IndividualFormSections } from '@/components/tax-form/IndividualFormSections';
import { BusinessFormSections } from '@/components/tax-form/BusinessFormSections';
import { INDIVIDUAL_SECTION_KEYS } from '@/components/tax-form/individualFormConfig';
import { BUSINESS_SECTION_KEYS } from '@/components/tax-form/businessFormConfig';
import { ArrowLeft, ChevronLeft, ChevronRight, Loader2, Trash2 } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';

function extractFormData(res: FormResponse): IndividualFormData | BusinessFormData {
  const { id: _id, filingType: _ft, ...rest } = res;
  return rest as IndividualFormData | BusinessFormData;
}

export default function TaxFormEditorPage() {
  const { formId } = useParams<{ formId: string }>();
  const navigate = useNavigate();
  const { selectedYear } = useTaxYear();
  const [form, setForm] = useState<FormResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [savingSection, setSavingSection] = useState<string | null>(null);
  const [currentSectionIndex, setCurrentSectionIndex] = useState(0);
  const [deleting, setDeleting] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const loadForm = useCallback(async () => {
    if (!formId) return;
    try {
      const data = await getFormById(formId);
      setForm(data);
    } catch {
      toast.error('Failed to load form');
      navigate('/tax-form');
    } finally {
      setLoading(false);
    }
  }, [formId, navigate]);

  useEffect(() => {
    loadForm();
  }, [loadForm]);

  const handleSaveSection = useCallback(
    async (key: string, payload: unknown) => {
      if (!formId || !form) return;
      setSavingSection(key);
      try {
        const updated = await updateForm(formId, { [key]: payload });
        setForm((prev) => (prev ? { ...prev, ...extractFormData(updated) } : prev));
        toast.success('Section saved');
      } catch {
        toast.error('Failed to save section');
        setSavingSection(null);
        throw new Error('Failed to save section');
      } finally {
        setSavingSection(null);
      }
    },
    [formId, form]
  );

  const isIndividual = form?.filingType === FORM_FILING_TYPE.INDIVIDUAL;
  const sectionKeys = isIndividual ? INDIVIDUAL_SECTION_KEYS : BUSINESS_SECTION_KEYS;
  const totalSections = sectionKeys.length;
  const progressPercent = totalSections > 0 ? ((currentSectionIndex + 1) / totalSections) * 100 : 0;

  const goNext = useCallback(() => {
    setCurrentSectionIndex((i) => (i < totalSections - 1 ? i + 1 : i));
  }, [totalSections]);
  const goPrev = useCallback(() => {
    setCurrentSectionIndex((i) => (i > 0 ? i - 1 : i));
  }, []);

  const formDataForSave = form ? extractFormData(form) : null;
  const isLastSection = currentSectionIndex >= totalSections - 1;
  const handleSaveAndNext = useCallback(async () => {
    if (!formDataForSave) return;
    const key = sectionKeys[currentSectionIndex];
    const payload = formDataForSave[key as keyof typeof formDataForSave];
    await handleSaveSection(key, payload);
    if (!isLastSection) goNext();
  }, [formDataForSave, currentSectionIndex, sectionKeys, handleSaveSection, goNext, isLastSection]);

  const handleConfirmDeleteForm = useCallback(async () => {
    if (!formId || !form || form.filingType !== FORM_FILING_TYPE.BUSINESS) return;
    setDeleting(true);
    try {
      await deleteForm(formId);
      toast.success('Business form deleted');
      navigate('/tax-form');
    } catch {
      toast.error('Failed to delete form');
    } finally {
      setDeleting(false);
      setShowDeleteConfirm(false);
    }
  }, [formId, form, navigate]);

  if (loading || !form) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  const formData = extractFormData(form);

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <Button
            variant="ghost"
            size="sm"
            className="mb-2 -ml-2"
            onClick={() => navigate('/tax-form')}
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to tax forms
          </Button>
          <h1 className="text-2xl font-bold tracking-tight">
            {isIndividual
              ? 'Individual'
              : (formData as BusinessFormData).formName?.trim() || 'Business'}{' '}
            tax form — {selectedYear}
          </h1>
          <p className="text-muted-foreground mt-1">
            Complete each section and use &quot;Save and Next&quot; to save and move forward.
          </p>
        </div>
        {!isIndividual && (
          <Button
            variant="ghost"
            size="sm"
            className="text-destructive hover:text-destructive"
            onClick={() => setShowDeleteConfirm(true)}
            disabled={deleting}
            title="Delete this business form"
          >
            <Trash2 className="mr-2 h-4 w-4" />
            Delete form
          </Button>
        )}
      </div>

      <div className="space-y-2">
        <div className="flex items-center justify-between text-sm text-muted-foreground">
          <span>Section {currentSectionIndex + 1} of {totalSections}</span>
          <span>{Math.round(progressPercent)}%</span>
        </div>
        <Progress value={progressPercent} className="h-2" />
      </div>

      {isIndividual ? (
        <IndividualFormSections
          data={formData as IndividualFormData}
          onChange={(data) =>
            setForm((prev) => (prev ? { ...prev, ...data } : prev))
          }
          onSaveSection={handleSaveSection}
          savingSection={savingSection}
          currentSectionIndex={currentSectionIndex}
          onNext={goNext}
          onPrev={goPrev}
          isFirstSection={currentSectionIndex === 0}
          isLastSection={currentSectionIndex === totalSections - 1}
        />
      ) : (
        <BusinessFormSections
          data={formData as BusinessFormData}
          onChange={(data) =>
            setForm((prev) => (prev ? { ...prev, ...data } : prev))
          }
          onSaveSection={handleSaveSection}
          savingSection={savingSection}
          currentSectionIndex={currentSectionIndex}
          onNext={goNext}
          onPrev={goPrev}
          isFirstSection={currentSectionIndex === 0}
          isLastSection={currentSectionIndex === totalSections - 1}
        />
      )}

      <div className="flex flex-wrap items-center gap-2 pt-2 border-t">
        <Button
          variant="outline"
          size="sm"
          onClick={goPrev}
          disabled={currentSectionIndex === 0}
        >
          <ChevronLeft className="mr-1 h-4 w-4" />
          Previous
        </Button>
        <Button
          size="sm"
          onClick={handleSaveAndNext}
          disabled={!!savingSection}
        >
          {savingSection ? (
            <>
              <Loader2 className="mr-1 h-4 w-4 animate-spin" />
              Saving…
            </>
          ) : isLastSection ? (
            'Save'
          ) : (
            <>
              Save and Next
              <ChevronRight className="ml-1 h-4 w-4" />
            </>
          )}
        </Button>
      </div>

      {!isIndividual && (
        <ConfirmDialog
          open={showDeleteConfirm}
          onOpenChange={setShowDeleteConfirm}
          title="Delete business form"
          description="Are you sure you want to delete this business form? This cannot be undone."
          confirmLabel="Delete"
          variant="destructive"
          loading={deleting}
          onConfirm={handleConfirmDeleteForm}
        />
      )}
    </div>
  );
}
